// Mirrors app/models/gallery_theme.rb so the manager and admin forms can show
// what a colour will actually look like before anyone saves it.
//
// Deliberately nothing but relative luminance and a ratio — no iteration, no
// HSL round-trip — because there is no JS test runner in this project, so
// anything duplicated here has to be simple enough that it cannot quietly drift
// from the Ruby.
//
// The Ruby spec pins this table; if it ever changes, change it here too:
//   #E10600 -> 3.88 vs background, ink #FFFFFF
//   #FF6A00 -> 6.72 vs background, ink #0E0E10
//   #FFD400 -> 13.47 vs background, ink #0E0E10
//   #00C853 -> 8.62 vs background, ink #0E0E10
//   #8C4DFF -> 4.24 vs background, ink #FFFFFF
//   #1B2A5C -> 1.41 vs background, rejected
//   #E10600 + #FFD400 -> ink #0E0E10 (white would be 1.4:1 at the yellow end)

export const BACKGROUND = "#0E0E10";
export const WHITE = "#FFFFFF";
export const MIN_BACKGROUND_CONTRAST = 3;
/** Final Bout red — what a gallery paints when nothing above it has an opinion. */
export const DEFAULT_ACCENT = "#E10600";

export function luminance(hex: string): number {
  const raw = hex.replace("#", "");
  const [r, g, b] = [0, 2, 4]
    .map((i) => parseInt(raw.slice(i, i + 2), 16) / 255)
    .map((c) =>
      c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4),
    );

  return r * 0.2126 + g * 0.7152 + b * 0.0722;
}

export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** Anything a person might type, reduced to #RRGGBB — or null. */
export function normalizeAccent(value: string): string | null {
  let raw = value.trim().replace(/^#/, "");
  if (raw.length === 3) {
    raw = raw
      .split("")
      .map((c) => c + c)
      .join("");
  }

  return /^[0-9a-f]{6}$/i.test(raw) ? `#${raw.toUpperCase()}` : null;
}

/** Whichever of white or the page's near-black stands further from the accent.
 *  This is the derivation the button cannot do without — white on red is
 *  4.97:1, white on the same page's green is 2.24:1.
 *
 *  Given two accents the label crosses both, so each candidate is judged by its
 *  worst end rather than its average. */
export function inkFor(accents: string[]): string {
  const worst = (against: string) =>
    Math.min(...accents.map((hex) => contrast(hex, against)));

  return worst(WHITE) >= worst(BACKGROUND) ? WHITE : BACKGROUND;
}

/** The CSS a filled accent surface paints with: one colour flat, two as the
 *  same left-to-right gradient the public page uses. */
export function fillFor(accents: string[]): string {
  return accents.length > 1
    ? `linear-gradient(90deg, ${accents[0]}, ${accents[1]})`
    : accents[0];
}

/** The sentence to show whoever typed this, or null when it is fine. The Ruby
 *  wording is the one that ships on a failed save; this is the same rule said
 *  early, so the two forms cannot disagree about what a good colour is. */
export function rejectionReason(value: string): string | null {
  const hex = normalizeAccent(value);
  if (hex === null) return "Use a colour like #E10600.";

  const ratio = contrast(hex, BACKGROUND);
  if (ratio >= MIN_BACKGROUND_CONTRAST) return null;

  return `Too dark to read against the gallery's background (${ratio.toFixed(1)}:1, needs ${MIN_BACKGROUND_CONTRAST}:1). Try a lighter shade.`;
}
